import React from 'react'

import styled, { css } from 'styled-components'

import { IconEnum, Icon, IconSizeEnum } from '@atoms/images/Icon'
import { Paragraph } from '@atoms/index'
import { ResponsivePXValue, theme } from '@client/components/Theme'

export interface IconSelectOption {
  value: string
  icon: IconEnum
  label: string
  disabled?: boolean
}

export interface IconSelectButtonGroupProps {
  options: IconSelectOption[]
  selected: string
  onChange: (value: string) => void
  className?: string
  iconColor?: string
  labelColor?: string
  style?: React.CSSProperties
  buttonStyle?: React.CSSProperties
  disabled?: boolean
}

const Row = styled.div`
  display: flex;
  flex-direction: row;
  ${ResponsivePXValue('justify-content', { mobile: 'space-between' })};
`

const IconSelectButtonStyled = styled.button<{ selected: boolean, $disabled?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  transition: border 0.2s, background 0.2s, color 0.2s;
  outline: none;
  width: 100%;
  border: 1.5px solid ${props => props.selected ? props.theme.colors.oranges.coral : props.theme.colors.whites.silver};
  background: ${props => props.selected ? props.theme.colors.oranges.coral : props.theme.colors.whites.pureWhite};
  color: ${props => props.selected ? props.theme.colors.whites.pureWhite : props.theme.colors.greys.mineshaft};
  box-shadow: ${props => props.selected ? '0 2px 8px rgba(255, 102, 0, 0.08)' : 'none'};
  ${ResponsivePXValue('padding', '16px 12px 10px 12px')};
  ${ResponsivePXValue('margin-right', { mobile: '8px', tablet: '24px', desktop: '24px' })};
  ${ResponsivePXValue('margin-bottom', { desktop: '12px' })};
  ${ResponsivePXValue('max-width', { mobile: '60px', tablet: '90px', desktop: '90px' })};
  ${ResponsivePXValue('min-width', { mobile: '60px', tablet: '90px', desktop: '90px' })};
  ${ResponsivePXValue('max-height', { mobile: '56px', tablet: '90px', desktop: '90px' })};
  ${ResponsivePXValue('height', { mobile: '56px', tablet: '90px', desktop: '90px' })};
  svg, img { ${ResponsivePXValue('margin-bottom', '8px')}; }

  &:last-child {
    ${ResponsivePXValue('margin-right', { mobile: '0', tablet: '24px', desktop: '24px' })};
  }

  ${props => props.$disabled
    ? css`
    pointer-events: none;
    opacity: 0.5;
    cursor: not-allowed;
  `
    : null}
`

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`

export const IconSelectButtonGroup: React.FC<IconSelectButtonGroupProps> = ({
  options,
  selected,
  onChange,
  className,
  iconColor,
  labelColor,
  style,
  buttonStyle,
  disabled = false,
}) => (
  <Row className={className} style={style}>
    {options.map(opt => {
      const isSelected = selected === opt.value
      return (
        <IconSelectButtonStyled
          key={opt.value}
          type="button"
          selected={isSelected}
          $disabled={disabled || opt.disabled}
          style={buttonStyle}
          onClick={() => !(disabled) && onChange(opt.value)}>
          <IconContainer>
            <Icon icon={opt.icon} color={iconColor || (isSelected ? theme.colors.whites.pureWhite : theme.colors.greys.mineshaft)} size={IconSizeEnum.SMALL} />
          </IconContainer>
          <Paragraph variant='p3' align='center' bold color={labelColor || (isSelected ? theme.colors.whites.pureWhite : theme.colors.greys.mineshaft)}>{opt.label}</Paragraph>
        </IconSelectButtonStyled>
      )
    })}
  </Row>
)
